import { Trans, useTranslation } from 'react-i18next';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetOverlay,
  SheetTitle,
} from '@parrot-software/design-system';
import { toast } from 'sonner';
import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { AnalyticsStatuses, GroupingActions } from '@subscriptions/presentation/constants';
import { SubscriptionBlockForm, UnsavedChangesDialog } from '@subscriptions/presentation/components/base';
import type { SubscriptionBlockFormValues } from '@subscriptions/presentation/components/base';
import {
  useSubscriptionAnalytics,
  useUnsavedChangesGuard,
  useUpdateBlockStores,
} from '@subscriptions/presentation/hooks';
import { resolveApiErrorCode, resolveSubscriptionErrorMessage } from '@subscriptions/presentation/utils';

const strongComponents = { strong: <strong className="font-semibold" /> };

interface EditSubscriptionBlockSheetProps {
  block: SubscriptionBlockViewDto;
  open: boolean;
  onClose: () => void;
  onOpenChange: (open: boolean) => void;
}

/**
 * EditSubscriptionBlockSheet wraps SubscriptionBlockForm inside a side Sheet
 * to edit a block's name and stores.
 *
 * Prefills the form with the block's current name and stores, and passes the
 * `blockUuid` so the multiselect includes the block's own stores as options.
 * On success it closes the sheet, toasts and lets the mutation refetch the
 * block detail.
 *
 * Every close path (cancel button, X, overlay click, ESC) is routed through
 * an unsaved-changes guard: when the form is dirty a confirmation dialog is
 * shown before discarding; a successful submit closes directly.
 *
 * @param {EditSubscriptionBlockSheetProps} props - Component properties
 * @returns {React.ReactElement} Sheet containing the subscription block form
 */
export const EditSubscriptionBlockSheet = ({
  block,
  open,
  onClose,
  onOpenChange,
}: EditSubscriptionBlockSheetProps): React.ReactElement => {
  const { t, i18n } = useTranslation('subscriptions');
  const { mutateAsync: updateBlockStores, isLoading } = useUpdateBlockStores();
  const { cancelClose, confirmClose, isConfirmOpen, requestClose, setIsDirty } = useUnsavedChangesGuard({ onClose });
  const { trackConfigurationFinished } = useSubscriptionAnalytics();

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(nextOpen);
      return;
    }
    requestClose();
  };

  const handleSubmit = async (values: SubscriptionBlockFormValues) => {
    const previous = new Set(block.stores.map((store) => store.storeUuid));
    const next = new Set(values.storeUuids);
    // Separate counts, not a delta: swapping one store for another nets to zero and would hide
    // both operations.
    const storesAdded = values.storeUuids.filter((uuid) => !previous.has(uuid)).length;
    const storesRemoved = block.stores.filter((store) => !next.has(store.storeUuid)).length;
    const common = {
      action: GroupingActions.EDIT,
      blockUuid: block.uuid,
      groupInvoices: values.groupInvoice,
      storesAdded,
      storesRemoved,
    };

    try {
      await updateBlockStores({
        blockUuid: block.uuid,
        dto: { name: values.name, storeUuids: values.storeUuids, groupInvoice: values.groupInvoice },
      });
      trackConfigurationFinished({ ...common, status: AnalyticsStatuses.SUCCESS });
      onClose();
      toast.success(t('blockEdit.success'));
    } catch (error) {
      trackConfigurationFinished({
        ...common,
        errorCode: resolveApiErrorCode(error),
        status: AnalyticsStatuses.FAILED,
      });
      toast.error(resolveSubscriptionErrorMessage(error, t, i18n, 'blockEdit.error'));
    }
  };

  return (
    <Sheet onOpenChange={handleOpenChange} open={open}>
      <SheetOverlay className="z-[1300] bg-black/50" />
      <SheetContent className="w-full max-w-full sm:w-[385px] sm:max-w-[385px] flex flex-col p-0 font-sans z-[1350]">
        <SheetHeader className="shrink-0 p-4 text-left">
          <SheetTitle className="text-lg">{t('blockEdit.sheet.title')}</SheetTitle>
          <SheetDescription>
            <Trans components={strongComponents} i18nKey="subscriptions:blockEdit.sheet.description" />
          </SheetDescription>
        </SheetHeader>

        <SubscriptionBlockForm
          blockUuid={block.uuid}
          defaultValues={{
            name: block.name,
            storeUuids: block.stores.map((store) => store.storeUuid),
            groupInvoice: block.groupInvoice,
          }}
          isSubmitting={isLoading}
          onCancel={requestClose}
          onDirtyChange={setIsDirty}
          onSubmit={handleSubmit}
        />

        <UnsavedChangesDialog onCancel={cancelClose} onConfirm={confirmClose} open={isConfirmOpen} />
      </SheetContent>
    </Sheet>
  );
};
